<template>
  <jade-box class="eu-fade-in !mt-6 !mb-3">
    <form
      ref="boxForm"
      action="javascript:void(0)"
      @submit.prevent="submit"
      novalidate="true"
    >
      <jade-group-header :title="title">
        <template #trailing>
          <jade-button
            icon="action-edit"
            size="small"
            variant="primary-ghost"
            v-show="disabled"
            @click="handleClickEditBtnEdit"
            v-if="showButtonEdit"
          >
            {{ $t('account.settings.edit') }}
          </jade-button>
        </template>
      </jade-group-header>

      <div
        v-if="loading"
        class="eu-pa_6 eu-align_center"
      >
        <jade-spinner />
      </div>
      <div
        v-else
        class="flex flex-col gap-5"
      >
        <div
          class="mx-5"
          v-if="(showAlert && !hiddenAlert) || showAlertInfo"
        >
          <jade-alert
            :variant="formError ? 'negative' : 'positive'"
            v-if="showAlert && !hiddenAlert"
            showIcon
            :title="
              formError
                ? $t('account.settings.alert.error-title')
                : $t('account.settings.alert.success-title')
            "
            :onDismiss="true"
            @onDismiss="closeAlert"
          >
            <div v-if="formError">
              <p
                v-for="(error, index) in errors"
                :key="index"
              >
                {{ error }}
              </p>
            </div>
            <p v-else>{{ $t('account.settings.saved-success') }}</p>
          </jade-alert>

          <jade-alert
            v-if="showAlertInfo"
            showIcon
            variant="info"
            :title="$t('account.settings.alert.info-title')"
          >
            {{ $t('account.settings.alert.info-content') }}
          </jade-alert>
        </div>

        <component
          :is="as"
          :disabled="disabled"
        >
          <legend class="eu-display_none">{{ title }}</legend>
          <div class="p-5">
            <slot name="content"></slot>
          </div>
          <div v-if="hasPassword">
            <hr
              class="emd-divider m-5 mt-0"
              v-show="!disabled"
            />
            <password-confirmation
              class="px-5"
              v-model="password"
              v-show="!disabled"
            ></password-confirmation>
          </div>
        </component>
      </div>
      <div
        class="flex justify-end gap-3 p-4 pt-0"
        v-show="!disabled"
        v-if="isEditable"
      >
        <jade-button
          variant="neutral-ghost"
          type="button"
          @click="cancelEdit"
          :disabled="isLoading"
        >
          {{ $t('account.settings.cancel') }}
        </jade-button>

        <jade-button
          variant="primary-solid"
          tabindex="0"
          type="submit"
          :disabled="submitButtoniIsDisabled"
        >
          <jade-spinner v-if="submitButtoniIsLoading" />
          <span v-else>{{ $t('account.settings.save-updates') }}</span>
        </jade-button>
      </div>
    </form>
    <emd-confirm
      v-if="hasConfirm"
      ref="confirmModal"
      @confirm="submitConfirm"
      :message="messageConfirm"
    />
  </jade-box>
</template>

<script>
import { EmdConfirm } from 'emerald-vue/src/components'
import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'
import { formatErrorMessage } from '@/support/formatErrorMessage'
import { mapActions, mapGetters } from 'vuex'
import {
  JadeBox,
  JadeGroupHeader,
  JadeButton,
  JadeAlert,
  JadeSpinner
} from '@jade'

export default {
  name: 'ConfigBox',
  components: {
    PasswordConfirmation,
    EmdConfirm,
    JadeBox,
    JadeGroupHeader,
    JadeButton,
    JadeAlert,
    JadeSpinner
  },
  props: {
    callback: Function,
    title: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    formIsInvalid: {
      type: Boolean,
      required: false,
      default: false
    },
    hideEditButton: {
      type: Boolean,
      required: false,
      default: false
    },
    hasConfirm: {
      type: Boolean,
      default: false,
      required: false
    },
    messageConfirm: {
      type: String,
      required: false
    },
    hasPassword: {
      type: Boolean,
      required: false,
      default: true
    },
    mfaNeeded: {
      type: Boolean,
      required: false,
      default: false
    },
    alertInfoFixed: {
      type: Boolean,
      default: false
    },
    as: {
      type: String,
      default: 'fieldset'
    },
    hiddenAlert: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['merchantId', 'accountId']),
    disabledSubmit () {
      if (!this.hasPassword) return false
      return !this.password
    },
    params () {
      return {
        merchantId: this.merchantId,
        accountId: this.accountId,
        entityId: this.accountId,
        refresh: true
      }
    },
    isEditable () {
      if (this.hideEditButton || this.mfaNeeded) return false
      return this.permissionIsEnabled('settings.account:edit')
    },
    showButtonEdit () {
      return this.isEditable || this.mfaNeeded
    },
    handleClickEditBtnEdit () {
      return this.isEditable ? this.handleClickEditBtn : this.openConfigMfaModal
    },
    submitButtoniIsLoading () {
      return this.isLoading || this.loading
    },
    submitButtoniIsDisabled () {
      return (
        this.disabledSubmit || this.formIsInvalid || this.submitButtoniIsLoading
      )
    }
  },
  data () {
    return {
      disabled: true,
      password: '',
      isLoading: false,
      showAlert: false,
      showAlertInfo: false,
      formError: false,
      errors: [],
      label: 'webhook.form.confirm-change'
    }
  },
  methods: {
    ...mapActions({
      setConfigMfaModal: 'SET_CONFIG_MFA_MODAL'
    }),
    getEncriptyPassword () {
      return window.btoa(this.password)
    },
    openConfigMfaModal () {
      this.setConfigMfaModal(true)
    },
    handleClickEditBtn () {
      this.disabled = !this.disabled
      this.$emit('clickEditButton', this.disabled)
      if (this.alertInfoFixed) this.showAlertInfo = true
    },
    submit (e) {
      if (this.$listeners.submit)
        return this.$emit('submit', this.getEncriptyPassword())
      if (this.hasConfirm) this.$refs.confirmModal.open()
      else this.submitContent()
    },
    submitConfirm (val) {
      if (val) this.submitContent()
      else this.cancelEdit()
    },
    submitContent () {
      const hashedPassword = this.getEncriptyPassword()
      this.isLoading = true
      this.showAlert = false
      this.showAlertInfo = false
      this.disabled = true
      const msgErrorInternal = this.$t('account.settings.error-internal')
      this.callback(hashedPassword)
        .then(res => {
          this.isLoading = false
          this.showAlert = true
          this.formError = false
          this.password = ''
          this.$store.dispatch('GET_ACCOUNT', this.params)
          this.$store.dispatch('GET_ATTEMPT_SETTINGS', this.params)
          this.$store.dispatch('GET_LAST_LOG', this.params)
          this.$refs.boxForm.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          })
        })
        .catch(err => {
          this.formError = true
          this.isLoading = false
          this.showAlert = true
          this.showAlertInfo = false
          this.$refs.boxForm.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          })
          this.errors = formatErrorMessage(err, msgErrorInternal)
        })
    },
    cancelEdit () {
      this.isLoading = false
      this.formError = false
      this.showAlert = false
      this.showAlertInfo = false
      this.password = ''
      this.disabled = true
      this.$emit('cancelEdit')
    },
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    },
    toogleInfoFunction () {
      this.showAlertInfo = !this.showAlertInfo
    },
    fixedInfoFunction () {
      this.showAlertInfo = true
    },
    closeAlert () {
      this.showAlert = false
    }
  }
}
</script>
